import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GoogleSignInButton from "@/components/shared/GoogleSignInButton";

const Register = () => {
  const [useEmail, setUseEmail] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ name, email, password });
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white grid place-items-center text-primary font-bold">P</div>
          <span className="font-display text-xl font-bold">PAYD</span>
        </Link>
        <div>
          <h2 className="text-4xl font-bold tracking-tight leading-tight">One account to pay and get paid.</h2>
          <p className="mt-4 opacity-90">Create a contract with anyone, on either side. PAYD handles the escrow.</p>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">© PAYD {new Date().getFullYear()}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center text-primary-foreground font-bold">P</div>
            <span className="font-display text-xl font-bold">PAYD</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Create account</h1>
          <p className="mt-2 text-sm text-muted-foreground">No separate accounts for paying or getting paid.</p>

          <div className="mt-6">
            <GoogleSignInButton />
          </div>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {!useEmail ? (
            <Button variant="ghost" onClick={() => setUseEmail(true)} className="w-full" data-testid="use-email-register-btn">
              Use email instead
            </Button>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="register-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="register-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11" data-testid="register-submit-btn">
                {loading ? "Creating…" : "Create account"}
              </Button>
            </form>
          )}

          <div className="mt-8 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">Sign in</Link>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and{" "}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
